import { useCallback, useEffect, useState } from 'react';
import {
  createNotesLockVerifier,
  decryptNoteBody,
  deriveNotesLockKey,
  encryptNoteBody,
  generateNotesLockSalt,
  getNotesLockSessionKey,
  setNotesLockSessionKey,
  clearNotesLockSession,
  touchNotesLockSession,
  verifyNotesLockPassword,
} from '@trove/note-lock-crypto';
import {
  changeNotesLockPassword,
  fetchNotesLockConfig,
  resetNotesLockPassword,
  setupNotesLockPassword,
  type NotesLockConfig,
} from '@/api/notesLock';

export function useNotesLock() {
  const [config, setConfig] = useState<NotesLockConfig>({ configured: false });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const cfg = await fetchNotesLockConfig();
      setConfig(cfg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const verifyAndDeriveKey = useCallback(
    async (password: string): Promise<CryptoKey> => {
      if (!config.notesLockSalt || !config.notesLockVerifier) {
        throw new Error('Notes lock password is not configured.');
      }
      const ok = await verifyNotesLockPassword(
        password,
        config.notesLockSalt,
        config.notesLockVerifier
      );
      if (!ok) throw new Error('Incorrect Notes lock password.');
      const key = await deriveNotesLockKey(password, config.notesLockSalt);
      setNotesLockSessionKey(key);
      return key;
    },
    [config]
  );

  const unlockWithPassword = useCallback(
    async (password: string, notesCipher: string): Promise<string> => {
      const key = await verifyAndDeriveKey(password);
      return decryptNoteBody(notesCipher, key);
    },
    [verifyAndDeriveKey]
  );

  const setupPassword = useCallback(
    async (password: string, confirm: string) => {
      if (password !== confirm) throw new Error('Passwords do not match.');
      if (password.length < 8) throw new Error('Password must be at least 8 characters.');
      const salt = generateNotesLockSalt();
      const verifier = await createNotesLockVerifier(password, salt);
      await setupNotesLockPassword({ password, notesLockSalt: salt, notesLockVerifier: verifier });
      const key = await deriveNotesLockKey(password, salt);
      setNotesLockSessionKey(key);
      await refresh();
    },
    [refresh]
  );

  const changePassword = useCallback(
    async (currentPassword: string, newPassword: string, confirm: string) => {
      if (newPassword !== confirm) throw new Error('Passwords do not match.');
      await verifyAndDeriveKey(currentPassword);
      const salt = generateNotesLockSalt();
      const verifier = await createNotesLockVerifier(newPassword, salt);
      await changeNotesLockPassword({ notesLockSalt: salt, notesLockVerifier: verifier });
      clearNotesLockSession();
      const key = await deriveNotesLockKey(newPassword, salt);
      setNotesLockSessionKey(key);
      await refresh();
    },
    [refresh, verifyAndDeriveKey]
  );

  const resetPassword = useCallback(async () => {
    await resetNotesLockPassword();
    clearNotesLockSession();
    await refresh();
  }, [refresh]);

  const encryptBodyWithPassword = useCallback(
    async (html: string, password: string): Promise<string> => {
      const key = await verifyAndDeriveKey(password);
      return encryptNoteBody(html, key);
    },
    [verifyAndDeriveKey]
  );

  const encryptBodyWithSession = useCallback(async (html: string): Promise<string> => {
    const key = getNotesLockSessionKey();
    if (!key) throw new Error('Notes lock session expired. Enter your password.');
    touchNotesLockSession();
    return encryptNoteBody(html, key);
  }, []);

  const decryptBody = useCallback(
    async (notesCipher: string, password?: string): Promise<string> => {
      if (password) return unlockWithPassword(password, notesCipher);
      const key = getNotesLockSessionKey();
      if (!key) throw new Error('Notes lock session expired. Enter your password.');
      touchNotesLockSession();
      return decryptNoteBody(notesCipher, key);
    },
    [unlockWithPassword]
  );

  const hasActiveSession = useCallback(() => Boolean(getNotesLockSessionKey()), []);

  return {
    config,
    loading,
    refresh,
    setupPassword,
    changePassword,
    resetPassword,
    unlockWithPassword,
    encryptBodyWithPassword,
    encryptBodyWithSession,
    decryptBody,
    hasActiveSession,
    clearSession: clearNotesLockSession,
    verifyAndDeriveKey,
  };
}
